import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import HeaderDateFilters from './Tasks/components/Filters/HeaderDateFilters';
import BarChart from '../components/charts/BarChart';
import MultiLineChart from '../components/charts/MultiLineChart';
import { FiUsers, FiClipboard, FiClock, FiShoppingCart, FiTrendingUp, FiMapPin, FiAlertTriangle, FiCheck, FiCreditCard, FiActivity } from 'react-icons/fi';
import { TbCurrencyRupee } from 'react-icons/tb';

const OperationsDashboard = () => {
  const { isAuthenticated, company } = useAuth();
  const [stats, setStats] = useState(null);
  const [bizStats, setBizStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [selectedDateRange, setSelectedDateRange] = useState(() => {
    const now = new Date();
    const fyStartMonth = 3; // April (0-indexed)
    const fyStartYear = now.getMonth() >= fyStartMonth ? now.getFullYear() : now.getFullYear() - 1;
    const startDate = `${fyStartYear}-04-01`;
    const endDate = `${fyStartYear + 1}-03-31`;
    return { startDate, endDate, label: 'Current FY' };
  });

  // ─── Matte palette (matching AccountsDashboard) ───────────────────────────
  const matte = useMemo(() => ({
    blue: '#74a0d6',
    purple: '#8a7bb8',
    green: '#79b38a',
    red: '#c57878',
    amber: '#e39a2b',
    teal: '#55a39b',
    rose: '#b77a98',
    indigo: '#7a76b5',
    allGrey: '#9ca3af',
    rust: '#b3784f'
  }), []);

  const formatInr = (n) => {
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(n || 0));
    } catch {
      return `₹${Number(n || 0).toFixed(0)}`;
    }
  };

  const toDateStr = (val) => {
    if (!val) return undefined;
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return undefined;
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return val.slice(0, 10);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const statsPromise = api.get('/api/company/stats');
        const fromParam = toDateStr(selectedDateRange?.startDate);
        const toParam = toDateStr(selectedDateRange?.endDate);
        const bizStatsPromise = api.get('/api/company/business-stats', { params: { fromDate: fromParam, toDate: toParam } });
        const trendsPromise = api.get('/api/company/business-trends', { params: { fromDate: fromParam, toDate: toParam } });
        const [statsRes, bizRes, trendsRes] = await Promise.allSettled([statsPromise, bizStatsPromise, trendsPromise]);
        if (statsRes.status === 'fulfilled') setStats(statsRes.value.data.data);
        if (bizRes.status === 'fulfilled') setBizStats(bizRes.value.data.data);
        if (trendsRes.status === 'fulfilled') setTrends(trendsRes.value.data.data);
      } catch (e) {
        setError('Failed to load operations overview');
      } finally {
        setLoading(false);
      }
    };
    if (isAuthenticated && company?.id) fetchData();
  }, [isAuthenticated, company?.id, selectedDateRange?.startDate, selectedDateRange?.endDate]);

  // ─── Computed values ─────────────────────────────────────────────────────
  const ordersPerDay = useMemo(() => trends?.ordersPerDay || [], [trends]);
  const revenuePerDay = useMemo(() => trends?.revenuePerDay || [], [trends]);
  const totalOrders = useMemo(() => ordersPerDay.reduce((s, d) => s + (d.count || 0), 0), [ordersPerDay]);
  const totalRevenue = useMemo(() => revenuePerDay.reduce((s, d) => s + (d.amount || 0), 0), [revenuePerDay]);
  const avgOrdersPerDay = useMemo(() => ordersPerDay.length ? Math.round(totalOrders / ordersPerDay.length) : 0, [ordersPerDay, totalOrders]);
  const latestDayOrders = useMemo(() => ordersPerDay.length ? (ordersPerDay[ordersPerDay.length - 1].count || 0) : 0, [ordersPerDay]);
  const latestDayRevenue = useMemo(() => revenuePerDay.length ? (revenuePerDay[revenuePerDay.length - 1].amount || 0) : 0, [revenuePerDay]);
  const peakOrdersDay = useMemo(() => {
    if (!ordersPerDay.length) return null;
    const max = Math.max(...ordersPerDay.map(d => d.count || 0));
    return ordersPerDay.find(d => d.count === max) || null;
  }, [ordersPerDay]);

  // ─── Components ──────────────────────────────────────────────────────────
  const Card = ({ title, subtitle, children }) => (
    <div style={{
      borderRadius: 12,
      padding: 12,
      background: 'linear-gradient(180deg, #1a1a1a 0%, #0d1117 100%)',
      border: '1px solid rgba(255,255,255,0.08)',
      boxShadow: '0 10px 25px rgba(0,0,0,0.12)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <div style={{ fontWeight: 400, color: '#fff', fontSize: 13 }}>{title}</div>
        <div style={{ color: 'rgba(255,255,255,0.78)', fontSize: 11 }}>{subtitle}</div>
      </div>
      <div style={{ marginTop: 10 }}>{children}</div>
    </div>
  );

  const StatItem = ({ icon, label, value, color, onClick, subtitle }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        color: '#fff',
        padding: '10px 12px',
        borderRadius: 10,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        textAlign: 'left'
      }}
      title={subtitle}
    >
      <div style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        background: `${color}20`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color,
        flexShrink: 0
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 16, fontWeight: 500, color: '#fff' }}>
          {value === null || value === undefined || (typeof value === 'number' && Number.isNaN(value)) ? '-' : value}
        </div>
      </div>
    </button>
  );

  const SegBar = ({ segments }) => {
    const totalSeg = segments.reduce((a, s) => a + Number(s.value || 0), 0) || 1;
    return (
      <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.08)' }}>
        {segments.map((s, i) => (
          <button
            key={i}
            type="button"
            onClick={s.onClick}
            title={s.title}
            style={{
              width: `${(Number(s.value || 0) / totalSeg) * 100}%`,
              background: s.color,
              border: 'none',
              padding: 0,
              cursor: s.onClick ? 'pointer' : 'default',
              opacity: 0.9,
              borderRadius: 0,
              borderTopLeftRadius: i === 0 ? 5 : 0,
              borderBottomLeftRadius: i === 0 ? 5 : 0,
              borderTopRightRadius: i === segments.length - 1 ? 5 : 0,
              borderBottomRightRadius: i === segments.length - 1 ? 5 : 0
            }}
          />
        ))}
      </div>
    );
  };

  const Legend = ({ items }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
      {items.map((it, i) => (
        <button
          key={i}
          type="button"
          onClick={it.onClick}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 10,
            width: '100%',
            background: 'rgba(255,255,255,0.06)',
            border: it.active ? '1px solid rgba(255,255,255,0.65)' : '1px solid rgba(255,255,255,0.10)',
            color: '#fff',
            padding: '8px 10px',
            borderRadius: 12,
            cursor: it.onClick ? 'pointer' : 'default'
          }}
          title={it.title}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.9)', fontSize: 12 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: it.color }} />
            {it.label}
          </span>
          <span style={{ fontWeight: 400 }}>{it.value}</span>
        </button>
      ))}
    </div>
  );

  // ─── Early returns ───────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className="page-container" style={{ background: 'linear-gradient(180deg, #0d1117 0%, #161b22 100%)', minHeight: '100vh' }}>
        <div className="page-content" style={{ background: 'transparent' }}>
          <h2 style={{ fontSize: 18, margin: 0, color: '#fff' }}>Operations</h2>
          <div style={{ color: 'rgba(255,255,255,0.6)', marginTop: 16 }}>Please log in to view operations dashboard</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container" style={{ background: 'linear-gradient(180deg, #0d1117 0%, #161b22 100%)', minHeight: '100vh' }}>
        <div className="page-content" style={{ background: 'transparent' }}>
          <h2 style={{ fontSize: 18, margin: 0, color: '#fff' }}>Operations</h2>
          <div style={{ 
            marginTop: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.5)',
            fontSize: 13
          }}>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ background: 'linear-gradient(180deg, #0d1117 0%, #161b22 100%)', minHeight: '100vh' }}>
      <style>{`
        .dark-date-filters .header-date-filters .drp-input {
          background: rgba(255,255,255,0.08) !important;
          border: 1px solid rgba(255,255,255,0.15) !important;
          color: #fff !important;
        }
        .dark-date-filters .header-date-filters .drp-input:hover {
          background: rgba(255,255,255,0.12) !important;
          border-color: rgba(255,255,255,0.25) !important;
        }
        .dark-date-filters .header-date-filters .date-separator {
          color: rgba(255,255,255,0.5) !important;
        }
        .dark-date-filters .header-date-filters .drp-clear {
          background: rgba(255,255,255,0.08) !important;
          border: 1px solid rgba(255,255,255,0.15) !important;
          color: #fff !important;
        }
        .dark-date-filters .header-date-filters .drp-clear:hover {
          background: rgba(255,255,255,0.15) !important;
        }
      `}</style>
      <div className="page-content" style={{ background: 'transparent' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ fontSize: 18, margin: 0, color: '#fff' }}>Operations Overview</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }} className="dark-date-filters">
            <HeaderDateFilters value={selectedDateRange} onChange={setSelectedDateRange} />
          </div>
        </div>

        {error && (
          <div style={{ 
            background: 'rgba(197, 120, 120, 0.15)', 
            border: '1px solid rgba(197, 120, 120, 0.3)', 
            borderRadius: 8, 
            padding: '10px 14px', 
            color: matte.red, 
            fontSize: 12,
            marginBottom: 12
          }}>
            {error}
          </div>
        )}

        {/* Main Stats Panel */}
        <div style={{
          background: 'linear-gradient(180deg, #1a1a1a 0%, #0d1117 100%)',
          borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
          padding: 12
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>

            {/* ─── Team & Activity Card ──────────────────────────────────── */}
            <Card title="Team & Activity" subtitle="Real-time + Period stats">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <StatItem
                  icon={<FiUsers size={16} />}
                  label="Active Users"
                  value={stats?.activeUsers ?? 0}
                  color={matte.blue}
                  subtitle="Checked-in today"
                />
                <StatItem
                  icon={<FiMapPin size={16} />}
                  label="Live Locations"
                  value={stats?.activeUsers ?? 0}
                  color={matte.teal}
                  onClick={() => navigate('/location')}
                  subtitle="Active users on map"
                />
              </div>

              {/* Period-filtered task stats */}
              <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Tasks (Selected Period)</div>
                </div>
                
                {/* Created vs Completed in period */}
                <SegBar segments={[
                  { value: bizStats?.tasksCreatedCount || 0, color: matte.blue, title: `Created: ${bizStats?.tasksCreatedCount || 0}`, onClick: () => navigate(`/tasks?startDate=${toDateStr(selectedDateRange?.startDate)}&endDate=${toDateStr(selectedDateRange?.endDate)}`) },
                  { value: bizStats?.tasksCompletedCount || 0, color: matte.green, title: `Completed: ${bizStats?.tasksCompletedCount || 0}`, onClick: () => navigate(`/tasks?status=resolved&startDate=${toDateStr(selectedDateRange?.startDate)}&endDate=${toDateStr(selectedDateRange?.endDate)}`) }
                ]} />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => navigate(`/tasks?startDate=${toDateStr(selectedDateRange?.startDate)}&endDate=${toDateStr(selectedDateRange?.endDate)}`)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      background: 'rgba(116, 160, 214, 0.12)',
                      border: '1px solid rgba(116, 160, 214, 0.25)',
                      color: '#fff',
                      padding: '8px 10px',
                      borderRadius: 12,
                      cursor: 'pointer'
                    }}
                    title="Tasks created in selected period"
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.9)', fontSize: 12 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: matte.blue }} />
                      Created
                    </span>
                    <span style={{ fontWeight: 400, color: matte.blue }}>{bizStats?.tasksCreatedCount ?? 0}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/tasks?status=resolved&startDate=${toDateStr(selectedDateRange?.startDate)}&endDate=${toDateStr(selectedDateRange?.endDate)}`)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      background: 'rgba(121, 179, 138, 0.12)',
                      border: '1px solid rgba(121, 179, 138, 0.25)',
                      color: '#fff',
                      padding: '8px 10px',
                      borderRadius: 12,
                      cursor: 'pointer'
                    }}
                    title="Tasks completed in selected period"
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.9)', fontSize: 12 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: matte.green }} />
                      Completed
                    </span>
                    <span style={{ fontWeight: 400, color: matte.green }}>{bizStats?.tasksCompletedCount ?? 0}</span>
                  </button>
                </div>
              </div>

              {/* Real-time task status */}
              <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Current Status (Real-time)</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                    Active: {stats?.activeTasks ?? 0}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => navigate('/tasks?status=pending,in-progress,incoming')}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.10)',
                      color: '#fff',
                      padding: '8px 10px',
                      borderRadius: 12,
                      cursor: 'pointer'
                    }}
                    title="Tasks in progress"
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.9)', fontSize: 12 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: matte.amber }} />
                      Active
                    </span>
                    <span style={{ fontWeight: 400 }}>{stats?.activeTasks ?? 0}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/tasks?status=pending,in-progress,incoming&due=overdue')}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      background: (stats?.overdueTasks ?? 0) > 0 ? 'rgba(197, 120, 120, 0.12)' : 'rgba(255,255,255,0.06)',
                      border: (stats?.overdueTasks ?? 0) > 0 ? '1px solid rgba(197, 120, 120, 0.25)' : '1px solid rgba(255,255,255,0.10)',
                      color: '#fff',
                      padding: '8px 10px',
                      borderRadius: 12,
                      cursor: 'pointer'
                    }}
                    title="Past due and pending"
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.9)', fontSize: 12 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: matte.red }} />
                      Overdue
                    </span>
                    <span style={{ fontWeight: 400, color: (stats?.overdueTasks ?? 0) > 0 ? matte.red : '#fff' }}>{stats?.overdueTasks ?? 0}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/tasks?status=pending,in-progress,incoming&date=today')}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.10)',
                      color: '#fff',
                      padding: '8px 10px',
                      borderRadius: 12,
                      cursor: 'pointer'
                    }}
                    title="Scheduled for today"
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.9)', fontSize: 12 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: matte.purple }} />
                      Due Today
                    </span>
                    <span style={{ fontWeight: 400 }}>{stats?.dueTasksToday ?? 0}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/tasks?status=resolved&date=today')}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.10)',
                      color: '#fff',
                      padding: '8px 10px',
                      borderRadius: 12,
                      cursor: 'pointer'
                    }}
                    title="Resolved today"
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.9)', fontSize: 12 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: matte.green }} />
                      Done Today
                    </span>
                    <span style={{ fontWeight: 400, color: matte.green }}>{stats?.completedTasksToday ?? 0}</span>
                  </button>
                </div>
              </div>
            </Card>

            {/* ─── Orders & Revenue Card ─────────────────────────────────── */}
            <Card title="Orders & Revenue" subtitle="Selected period">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{
                  background: 'rgba(121, 179, 138, 0.12)',
                  borderRadius: 10,
                  padding: '12px 14px',
                  border: '1px solid rgba(121, 179, 138, 0.2)'
                }}>
                  <div style={{ fontSize: 18, fontWeight: 600, color: matte.green }}>{totalOrders}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 2, textTransform: 'uppercase' }}>Total Orders</div>
                </div>
                <div style={{
                  background: 'rgba(116, 160, 214, 0.12)',
                  borderRadius: 10,
                  padding: '12px 14px',
                  border: '1px solid rgba(116, 160, 214, 0.2)'
                }}>
                  <div style={{ fontSize: 18, fontWeight: 600, color: matte.blue }}>{formatInr(totalRevenue)}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 2, textTransform: 'uppercase' }}>Total Revenue</div>
                </div>
              </div>

              <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(255,255,255,0.85)' }}>
                  <span>Latest day orders</span>
                  <span style={{ color: '#fff', fontWeight: 400 }}>{latestDayOrders}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(255,255,255,0.85)' }}>
                  <span>Latest day revenue</span>
                  <span style={{ color: '#fff', fontWeight: 400 }}>{formatInr(latestDayRevenue)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(255,255,255,0.85)' }}>
                  <span>Avg orders/day</span>
                  <span style={{ color: matte.amber, fontWeight: 400 }}>{avgOrdersPerDay}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(255,255,255,0.85)' }}>
                  <span>Avg revenue/day</span>
                  <span style={{ color: matte.teal, fontWeight: 400 }}>{ordersPerDay.length ? formatInr(totalRevenue / ordersPerDay.length) : '-'}</span>
                </div>
              </div>

              <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 10 }}>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>POS & Direct Sales</div>
                <Legend items={[
                  { label: 'Orders Received', value: bizStats?.ordersReceivedCount ?? 0, color: matte.green, onClick: () => navigate('/orders'), title: 'Orders in selected period' },
                  { label: 'POS Transactions', value: bizStats?.posSalesCount ?? 0, color: matte.blue, onClick: () => navigate('/invoices'), title: 'Paid POS invoices' },
                  { label: 'POS Revenue', value: formatInr(bizStats?.posAmountReceived || 0), color: matte.teal, onClick: () => navigate('/invoices'), title: 'Received via POS' },
                  { label: 'Peak day', value: peakOrdersDay ? `${peakOrdersDay.count} orders` : '-', color: matte.purple, title: peakOrdersDay?.date || 'N/A' }
                ]} />
              </div>
            </Card>
          </div>

          {/* ─── Trends Row ─────────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>

            {/* Orders Trend */}
            <Card title="Orders Trend" subtitle={peakOrdersDay ? `Peak: ${peakOrdersDay.date}` : 'Daily'}>
              <div style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                borderRadius: 10,
                padding: '12px 10px',
                border: '1px solid rgba(255,255,255,0.06)'
              }}>
                {ordersPerDay.length > 0 ? (
                  <>
                    <MultiLineChart
                      dark
                      height={180}
                      series={[
                        { name: 'Orders', color: matte.amber, data: ordersPerDay.map(d => ({ date: d.date, amount: d.count })) }
                      ]}
                    />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>
                        <span style={{ width: 12, height: 3, borderRadius: 2, background: matte.amber }} />
                        <span>Orders per day</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ 
                    height: 180, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: 'rgba(255,255,255,0.4)',
                    fontSize: 12
                  }}>
                    No order data for this period
                  </div>
                )}
              </div>

              {/* Footer stats */}
              <div style={{ marginTop: 12 }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: 6,
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: 8,
                  padding: '10px 8px'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                    <span style={{ fontWeight: 500, color: '#fff', fontSize: 13 }}>{totalOrders}</span>
                    <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, borderLeft: '1px solid rgba(255,255,255,0.08)', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
                    <span style={{ fontWeight: 500, color: '#fff', fontSize: 13 }}>{ordersPerDay.length}</span>
                    <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Days</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                    <span style={{ fontWeight: 500, color: '#fff', fontSize: 13 }}>{avgOrdersPerDay}</span>
                    <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Avg/day</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Revenue Trend */}
            <Card title="Revenue Trend" subtitle="Daily collections">
              <div style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                borderRadius: 10,
                padding: '12px 10px',
                border: '1px solid rgba(255,255,255,0.06)'
              }}>
                {revenuePerDay.length > 0 ? (
                  <>
                    <MultiLineChart
                      dark
                      height={180}
                      yCurrency
                      series={[
                        { name: 'Revenue', color: matte.green, data: revenuePerDay.map(d => ({ date: d.date, amount: d.amount })) }
                      ]}
                    />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>
                        <span style={{ width: 12, height: 3, borderRadius: 2, background: matte.green }} />
                        <span>Revenue per day</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ 
                    height: 180, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: 'rgba(255,255,255,0.4)',
                    fontSize: 12
                  }}>
                    No revenue data for this period
                  </div>
                )}
              </div>

              {/* Footer stats */}
              <div style={{ marginTop: 12 }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: 6,
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: 8,
                  padding: '10px 8px'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                    <span style={{ fontWeight: 500, color: '#fff', fontSize: 13 }}>{formatInr(totalRevenue)}</span>
                    <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, borderLeft: '1px solid rgba(255,255,255,0.08)', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
                    <span style={{ fontWeight: 500, color: '#fff', fontSize: 13 }}>{revenuePerDay.length}</span>
                    <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Days</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                    <span style={{ fontWeight: 500, color: '#fff', fontSize: 13 }}>{revenuePerDay.length ? formatInr(totalRevenue / revenuePerDay.length) : '-'}</span>
                    <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Avg/day</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperationsDashboard;
